// B6 — the Quality Assurance tab (tab key "legal-ops"; the legal-compliance
// content that used to live here relocated to the Compliance tab). Reads
// like an auditor's desk per the five-second rule: summary strip (last
// review, findings by severity, open exceptions, cadence) → review history
// (newest first, Draft → Completed ladder, report link when generated) →
// "Start QA review". Mutations are gated Compliance.QaReview (B5 / E4 /
// ADMIN); reads ride Loans.Read. The report ladder reuses the shared
// loan-report engine + preview slide-in with QA-scoped certify/file.

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Card, Skeleton } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import {
  useCertifyQaReport,
  useFileQaReport,
  useLoanQaOverview,
  useStartQaReview,
} from "@/hooks/portfolioAdmin/useQaReviews";
import { useLoanReports } from "@/hooks/portfolioAdmin/useLoanReports";
import type {
  LoanQaOverviewDto,
  LoanQaReviewDto,
} from "@/api/portfolioAdmin/qaReviewsApi";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";
import { Badge } from "@/design-system/components/ui";
import { LoanReportPreviewDrawer } from "../reports/LoanReportPreviewDrawer";
import { QaReviewDrawer } from "../qa/QaReviewDrawer";
import { QaScopeAreaCatalogModal } from "../qa/QaScopeAreaCatalogModal";
import { QaSeverityBadge } from "../qa/qaShared";
import { calendarDate, formatDate, daysUntil } from "../common/colorBand";

interface QualityAssuranceTabProps {
  loan: LoanSummaryDto;
}

export function QualityAssuranceTab({ loan }: QualityAssuranceTabProps) {
  const auth = useAuth();
  const canAct =
    auth.hasPermission("Compliance.QaReview") || auth.hasRoleCode("ADMIN");
  const canManageCatalog =
    auth.hasPermission("Loans.ManageQaCatalog") || auth.hasRoleCode("ADMIN");
  const [catalogOpen, setCatalogOpen] = useState(false);
  const toast = useToast();

  const overviewQuery = useLoanQaOverview(loan.id);
  const start = useStartQaReview(loan.id);

  // Deep-linkable drawer (?qaReview=<id>).
  const [params, setParams] = useSearchParams();
  const openReviewId = params.get("qaReview");
  const setOpenReviewId = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set("qaReview", id);
    else next.delete("qaReview");
    setParams(next, { replace: true });
  };

  const [openReportId, setOpenReportId] = useState<string | null>(null);
  const qaReports = useLoanReports(openReportId ? loan.id : undefined, "Qa");
  const certifyReport = useCertifyQaReport(loan.id);
  const fileReport = useFileQaReport(loan.id);

  // Close the drawer gracefully if its review disappears from the data —
  // but never off in-flight data (a just-started review's id lands only
  // after the overview refetch completes).
  const overview = overviewQuery.data;
  const openReview =
    overview?.reviews.find((r) => r.id === openReviewId) ?? null;
  useEffect(() => {
    if (openReviewId && overview && !openReview && !overviewQuery.isFetching)
      setOpenReviewId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openReviewId, overview, openReview, overviewQuery.isFetching]);

  const startReview = async () => {
    try {
      const review = await start.mutateAsync(undefined);
      setOpenReviewId((review as { id: string }).id);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  if (overviewQuery.isLoading) {
    return (
      <div className="flex flex-col gap-space-3" data-testid="qa-tab-loading">
        <Card>
          <Skeleton className="h-10 w-full" />
        </Card>
        <Card>
          <Skeleton className="h-24 w-full" />
        </Card>
      </div>
    );
  }

  if (overviewQuery.isError || !overview) {
    return (
      <Card>
        <p className="text-small text-status-error m-0" role="alert">
          Could not load QA reviews.
        </p>
        <Button
          variant="secondary"
          size="sm"
          className="mt-space-2"
          onClick={() => void overviewQuery.refetch()}
          data-testid="qa-tab-retry"
        >
          Retry
        </Button>
      </Card>
    );
  }

  const openReport =
    openReportId && qaReports.data
      ? qaReports.data.find((r) => r.id === openReportId) ?? null
      : null;

  return (
    <div className="flex flex-col gap-space-4" data-testid="qa-tab">
      {canManageCatalog && (
        <div className="flex justify-end">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setCatalogOpen(true)}
            data-testid="qa-manage-scope-areas"
          >
            Manage scope areas
          </Button>
        </div>
      )}

      <SummaryStrip overview={overview} />

      {overview.reviews.length === 0 ? (
        <Card data-testid="qa-empty">
          <p className="text-small text-gray-mid m-0">
            No QA reviews yet. Start the first review.
          </p>
          {canAct && (
            <Button
              variant="primary"
              size="sm"
              className="mt-space-2"
              onClick={() => void startReview()}
              loading={start.isPending}
              data-testid="qa-start-review-empty"
            >
              Start QA review
            </Button>
          )}
        </Card>
      ) : (
        <Card>
          <div className="mb-space-3 flex items-center justify-between gap-space-3">
            <div>
              <h3 className="font-display font-bold text-h3 m-0">
                Review history
              </h3>
              <p className="text-caption text-gray-mid m-0 mt-space-1">
                Newest first. Open a review for its scope, findings and report.
              </p>
            </div>
            {canAct && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => void startReview()}
                loading={start.isPending}
                data-testid="qa-start-review"
              >
                Start QA review
              </Button>
            )}
          </div>
          <ul className="m-0 list-none divide-y divide-border-light p-0">
            {overview.reviews.map((r) => (
              <ReviewRow
                key={r.id}
                review={r}
                onOpen={() => setOpenReviewId(r.id)}
                onOpenReport={(reportId) => setOpenReportId(reportId)}
              />
            ))}
          </ul>
        </Card>
      )}

      {openReview && (
        <QaReviewDrawer
          // Remount per review so the drawer re-hydrates (FIND-12).
          key={`${openReview.id}-${openReview.status}`}
          loanId={loan.id}
          review={openReview}
          canAct={canAct}
          open
          onClose={() => setOpenReviewId(null)}
          onOpenReport={(reportId) => setOpenReportId(reportId)}
        />
      )}

      {openReport && (
        <LoanReportPreviewDrawer
          loanId={loan.id}
          report={openReport}
          open
          onClose={() => setOpenReportId(null)}
          actions={{
            canAct,
            certify: (id) => certifyReport.mutateAsync(id),
            file: (id) => fileReport.mutateAsync(id),
            isPending: certifyReport.isPending || fileReport.isPending,
          }}
        />
      )}

      {canManageCatalog && (
        <QaScopeAreaCatalogModal
          open={catalogOpen}
          onClose={() => setCatalogOpen(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary strip (five-second rule: the big numbers lead)
// ---------------------------------------------------------------------------

function SummaryStrip({ overview }: { overview: LoanQaOverviewDto }) {
  // Its own component, so it reads the zone itself rather than taking it as a
  // prop. `cadenceDays` drives BOTH the figure and `cadenceTone` below it.
  const cadenceDays =
    overview.cadence != null ? daysUntil(overview.cadence.dueDate) : null;
  const cadenceTone =
    cadenceDays === null
      ? "text-gray-mid"
      : cadenceDays < 0
        ? "text-status-error"
        : cadenceDays <= 30
          ? "text-status-warning"
          : "text-gray-mid";

  return (
    <Card data-testid="qa-summary-strip">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-space-3">
        <Stat label="Last review">
          <span className="text-onyx">
            {overview.lastCompletedReviewDate
              ? formatDate(overview.lastCompletedReviewDate)
              : "Never"}
          </span>
        </Stat>
        <Stat label="Open exceptions">
          <span
            className={
              overview.openExceptionCount > 0
                ? "text-status-error"
                : "text-onyx"
            }
            data-testid="qa-open-exceptions"
          >
            {overview.openExceptionCount}
          </span>
        </Stat>
        <Stat label="Findings">
          <span className="text-onyx" data-testid="qa-findings-count">
            {overview.exceptionCount} exception
            {overview.exceptionCount === 1 ? "" : "s"} ·{" "}
            {overview.observationCount} observation
            {overview.observationCount === 1 ? "" : "s"}
          </span>
        </Stat>
        <Stat label="QA review due">
          <span className={cadenceTone} data-testid="qa-cadence-due">
            {overview.cadence
              ? `${calendarDate(overview.cadence.dueDate)}${
                  cadenceDays !== null && cadenceDays < 0 ? " (overdue)" : ""
                }`
              : "—"}
          </span>
        </Stat>
      </div>
    </Card>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="font-display text-caption uppercase tracking-label text-gray-mid">
        {label}
      </div>
      <div className="mt-[2px] font-display font-bold text-h3 tabular-nums">
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// History rows
// ---------------------------------------------------------------------------

function ReviewRow({
  review,
  onOpen,
  onOpenReport,
}: {
  review: LoanQaReviewDto;
  onOpen: () => void;
  onOpenReport: (reportId: string) => void;
}) {
  const exceptions = review.findings.filter((f) => f.severity === "Exception");
  const observations = review.findings.filter(
    (f) => f.severity === "Observation",
  );

  return (
    <li
      className="flex items-center gap-space-3 py-space-3"
      data-testid={`qa-review-row-${review.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-space-2">
          <span className="font-display font-semibold text-small">
            {formatDate(review.reviewDate)}
          </span>
          <Badge status={review.status === "Completed" ? "finalized" : "draft"}>
            {review.status}
          </Badge>
          {review.completionStamp && (
            <Badge
              status={review.completionStamp === "Full" ? "finalized" : "review"}
              data-testid={`qa-review-stamp-${review.id}`}
            >
              {review.completionStamp}
            </Badge>
          )}
          {exceptions.length > 0 && <QaSeverityBadge severity="Exception" />}
          <span className="text-caption text-gray-mid">
            {review.reviewedByUserName ?? "Unknown reviewer"}
          </span>
        </div>
        <div className="mt-space-1 text-caption text-gray-mid">
          {review.findings.length === 0
            ? "No findings"
            : `${exceptions.length} exception${exceptions.length === 1 ? "" : "s"}` +
              ` (${review.openExceptionCount} open) · ` +
              `${observations.length} observation${observations.length === 1 ? "" : "s"}`}
          {review.reportLoanReportId && (
            <>
              {" "}
              ·{" "}
              <button
                type="button"
                className="text-gold-shadow hover:underline"
                onClick={() => onOpenReport(review.reportLoanReportId!)}
                data-testid={`qa-review-report-${review.id}`}
              >
                Report ({review.reportStatus ?? "Draft"})
              </button>
            </>
          )}
        </div>
      </div>
      <Button
        variant="tertiary"
        size="sm"
        onClick={onOpen}
        data-testid={`qa-review-open-${review.id}`}
      >
        Open
      </Button>
    </li>
  );
}
